import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { RootState } from '@/redux/store';

import { createNewUserSchema } from './schema';

interface FormData {
  organizationCode: string;
  user: string;
  firstName: string;
  lastName: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

interface CreateSubAccountData {
  organizationCode: string;
  userName: string;
  password: string;
  user: {
    firstName: string;
    lastName: string;
    phone: string;
  };
}

const useNewUsers = (onSuccess?: () => void) => {
  const { profile } = useSelector((state: RootState) => state.parcel);
  const [showPassword, setShowPassword] = useState<{
    password: boolean;
    confirmPassword: boolean;
  }>({
    password: false,
    confirmPassword: false,
  });

  const [isCreating, setIsCreating] = useState(false);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(createNewUserSchema()),
    mode: 'onChange',
  });

  const onChgShowPassword = useCallback(
    (type: 'password' | 'confirmPassword') => {
      setShowPassword((prev) => ({ ...prev, [type]: !prev[type] }));
    },
    [setShowPassword],
  );

  React.useEffect(() => {
    const fetchOrganizations = async () => {
      setLoadingOrganizations(true);
      try {
        const http = initializeHttpProvider();
        const response = await http.userData.getOrganizations();

        if (response?.success && response?.data) {
          setOrganizations(response.data);
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);

        setOrganizations([
          { organizationCode: 'ORG001', name: 'Organización Principal', type: 'principal' },
        ]);
      }
      setLoadingOrganizations(false);
    };

    fetchOrganizations();
  }, []);

  const onSubmit = async (formData: FormData) => {
    setIsCreating(true);

    try {
      const phoneWithoutSpaces = formData.phone.replace(/\s/g, '');
      const formattedPhone = phoneWithoutSpaces.startsWith('+')
        ? phoneWithoutSpaces
        : `+${phoneWithoutSpaces}`;

      const userData: CreateSubAccountData = {
        organizationCode: formData.organizationCode,
        userName: formData.user,
        password: formData.password,
        user: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formattedPhone,
        },
      };

      const http = initializeHttpProvider();
      const response = await http.userData.registerSubAccount(userData);

      if (response?.success) {
        toast.success('¡Usuario creado exitosamente!', {
          position: 'top-right',
          autoClose: 3000,
        });

        reset();
        setShowPassword({ password: false, confirmPassword: false });

        setTimeout(() => {
          onSuccess?.();
        }, 500);
      } else {
        const errorMsg = response?.message || 'Error al crear el usuario';
        toast.error(errorMsg, {
          position: 'top-right',
          autoClose: 5000,
        });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error al crear el usuario';
      toast.error(errorMsg, {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return {
    register,
    errors,
    isValid: isValid && isDirty,
    setValue,

    showPassword,
    onChgShowPassword,

    onSubmit: handleSubmit(onSubmit),

    organizations,
    loadingOrganizations,

    isCreating,

    userCountry: profile.data?.country,
    phoneValue: watch('phone') || '',
  };
};

export default useNewUsers;
